const MAX_LINES = 3;
const ROOT_SELECTOR = ".product-item__variants--all, .proad-product-item__variants--all";
const VALUE_SELECTOR = ".product-item__variant-value, .proad-product-item__variant-value";
const MORE_SELECTOR = ".product-item__variants-more, .proad-product-item__variants-more";
const LABEL_SELECTOR = ".product-item__variants-more-label, .proad-product-item__variants-more-label";
const LAST_VISIBLE_CLASS = "is-last-visible";

// Roots already set up, kept in memory rather than in a data- flag: infinite scroll rebuilds the list
// through innerHTML and carousels clone slides, and both copy attributes onto elements nobody observes.
const initializedRoots = new WeakSet<HTMLElement>();
let clickHandlerAttached = false;
let fontsHooked = false;

export default function initVariantListCollapse(): void {
    attachClickHandler();

    const roots = Array.from(document.querySelectorAll<HTMLElement>(ROOT_SELECTOR))
        .filter(root => !initializedRoots.has(root) && root.querySelector(MORE_SELECTOR));

    roots.forEach(root => {
        initializedRoots.add(root);
        observeWidth(root);
    });
    // A rebuilt or cloned card that was expanded shows every value already; leave it expanded.
    collapseAll(roots.filter(root => !root.classList.contains("is-expanded")));
    // After the measuring pass: fonts start loading during the first layout, so only then is the status reliable.
    remeasureAfterFontsLoad();
}

// Lines are first measured at DOMContentLoaded, possibly in the fallback font (font-display: swap),
// and the ResizeObserver only reacts to width, so measure again once the web fonts are in.
function remeasureAfterFontsLoad(): void {
    if (fontsHooked || !document.fonts || document.fonts.status === "loaded") {
        return;
    }

    fontsHooked = true;
    document.fonts.ready.then(() => {
        const roots = Array.from(document.querySelectorAll<HTMLElement>(ROOT_SELECTOR))
            .filter(root => initializedRoots.has(root) && !root.classList.contains("is-expanded"));
        collapseAll(roots);
    });
}

// Shows everything, measures every card, then hides: reading and writing card by card
// would force one layout pass per card. Rebuilt cards arrive with their old cut, hence the reset.
function collapseAll(roots: HTMLElement[]): void {
    roots.forEach(showAll);
    const measured = roots.map(root => ({ root, hiddenFrom: findFirstHiddenIndex(root) }));
    measured.forEach(({ root, hiddenFrom }) => applyCollapse(root, hiddenFrom));
}

// One delegated listener instead of one per card: carousels clone slides without the listeners,
// which left "+ N more" dead on the clones.
function attachClickHandler(): void {
    if (clickHandlerAttached) {
        return;
    }

    clickHandlerAttached = true;
    document.addEventListener("click", event => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const button = target.closest<HTMLButtonElement>(MORE_SELECTOR);
        const root = button ? button.closest<HTMLElement>(ROOT_SELECTOR) : null;
        if (!button || !root) {
            return;
        }

        event.preventDefault();
        if (root.classList.contains("is-expanded")) {
            collapse(root, button);
            return;
        }

        expand(root, button);
    });
}

function observeWidth(root: HTMLElement): void {
    if (typeof ResizeObserver === "undefined") {
        return;
    }

    let lastWidth = root.clientWidth;
    const observer = new ResizeObserver(() => {
        if (root.classList.contains("is-expanded") || root.clientWidth === lastWidth) {
            return;
        }

        lastWidth = root.clientWidth;
        recollapse(root);
    });
    observer.observe(root);
}

function expand(root: HTMLElement, button: HTMLButtonElement): void {
    root.classList.add("is-expanded");
    button.setAttribute("aria-expanded", "true");
    setLabel(button, button.dataset.showLess);
    showAll(root);
    button.hidden = false;
}

function collapse(root: HTMLElement, button: HTMLButtonElement): void {
    root.classList.remove("is-expanded");
    button.setAttribute("aria-expanded", "false");
    recollapse(root);
}

function recollapse(root: HTMLElement): void {
    showAll(root);
    applyCollapse(root, findFirstHiddenIndex(root));
}

function showAll(root: HTMLElement): void {
    getValues(root).forEach(value => {
        value.hidden = false;
        value.classList.remove(LAST_VISIBLE_CLASS);
    });
}

function getValues(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(VALUE_SELECTOR));
}

function setLabel(button: HTMLButtonElement, text: string | undefined): void {
    const label = button.querySelector<HTMLElement>(LABEL_SELECTOR);
    if (label && text) {
        label.textContent = text;
    }
}

// Index of the first value past MAX_LINES, or -1 when everything fits. Expects all values visible.
function findFirstHiddenIndex(root: HTMLElement): number {
    const tops = getValues(root).map(value => value.offsetTop);
    const lineTops = Array.from(new Set(tops)).sort((a, b) => a - b);
    if (lineTops.length <= MAX_LINES) {
        return -1;
    }

    const lastVisibleTop = lineTops[MAX_LINES - 1];
    return tops.findIndex(top => top > lastVisibleTop);
}

function applyCollapse(root: HTMLElement, hiddenFrom: number): void {
    const button = root.querySelector<HTMLButtonElement>(MORE_SELECTOR);
    if (!button) {
        return;
    }

    if (hiddenFrom < 0) {
        button.hidden = true;
        return;
    }

    const values = getValues(root);
    values.forEach((value, index) => {
        value.hidden = index >= hiddenFrom;
        // No trailing comma on the last value left in front of "+ N more".
        value.classList.toggle(LAST_VISIBLE_CLASS, index === hiddenFrom - 1);
    });

    button.hidden = false;
    const template = button.dataset.showMore;
    setLabel(button, template ? template.replace("{0}", String(values.length - hiddenFrom)) : undefined);
}
